import { useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getProductImagesByStatus } from 'services/product';

// Map statuses to their cache group
const getStatusCacheGroup = (status) => {
  const statusLower = status?.toLowerCase();

  if (statusLower === 'pending' || statusLower === 'active') {
    return 'active-pending';
  }

  if (statusLower === 'archived' || statusLower === 'discontinued') {
    return 'archived-discontinued';
  }

  return statusLower; // fallback to original status
};

export const getProductImagesByStatusKey = ({ productId, status }) => {
  const cacheGroup = getStatusCacheGroup(status);

  return [
    'pim',
    'get-product-images-by-status',
    parseInt(productId),
    cacheGroup, // Use cache group instead of individual status
  ];
};

export const useGetProductImagesByStatus = ({
  status,
  productId,
  options = {},
}) => {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: getProductImagesByStatusKey({ status, productId }),
    queryFn: () =>
      getProductImagesByStatus({ productStatus: status, productId }),
    enabled: !!status && !!productId,
    staleTime: 30 * 60 * 1000,
    ...options,
  });

  const invalidateProductImagesByStatus = () => {
    queryClient.invalidateQueries({
      queryKey: getProductImagesByStatusKey({ status, productId }),
    });
  };

  return {
    initialProductImagery: useMemo(() => query.data?.data, [query.data]) || {},
    invalidateProductImagesByStatus,
  };
};
